import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'permissionPreview';

const PermissionPreviewContext = createContext(null);

function normalizeUserId(userId) {
  if (userId == null || userId === '') return null;
  const n = Number(userId);
  return Number.isFinite(n) ? n : null;
}

function normalizeEmployeeId(employeeId) {
  if (employeeId == null || employeeId === '') return null;
  const n = Number(employeeId);
  return Number.isInteger(n) && n > 0 ? n : null;
}

function readStoredPreview() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.permissions)) return null;
    return {
      permissions: parsed.permissions.filter((p) => typeof p === 'string'),
      label: typeof parsed.label === 'string' && parsed.label.trim()
        ? parsed.label.trim()
        : 'custom permissions',
      userId: normalizeUserId(parsed.userId),
      employeeId: normalizeEmployeeId(parsed.employeeId)
    };
  } catch {
    return null;
  }
}

function realUserCanPreview(realPermissions = []) {
  if (!Array.isArray(realPermissions)) return false;
  if (realPermissions.includes('superuser')) return true;
  return (
    realPermissions.includes('view_users') ||
    realPermissions.includes('grant_permissions') ||
    realPermissions.includes('revoke_permissions')
  );
}

export function PermissionPreviewProvider({ children, realPermissions = [] }) {
  const [preview, setPreview] = useState(() => readStoredPreview());

  const canPreview = realUserCanPreview(realPermissions);

  useEffect(() => {
    if (!canPreview && preview) {
      sessionStorage.removeItem(STORAGE_KEY);
      setPreview(null);
    }
  }, [canPreview, preview]);

  const startPreview = useCallback(({
    permissions = [],
    label = 'custom permissions',
    userId = null,
    employeeId = null
  } = {}) => {
    if (!realUserCanPreview(realPermissions)) return false;
    const normalizedUserId = normalizeUserId(userId);
    const next = {
      permissions: Array.isArray(permissions)
        ? [...new Set(permissions.filter((p) => typeof p === 'string'))]
        : [],
      label: typeof label === 'string' && label.trim() ? label.trim() : 'custom permissions',
      userId: normalizedUserId,
      // When previewing a specific user, adopt their employee link (including null).
      employeeId: normalizedUserId != null
        ? normalizeEmployeeId(employeeId)
        : null
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setPreview(next);
    return true;
  }, [realPermissions]);

  const stopPreview = useCallback(() => {
    const userId = preview?.userId ?? null;
    sessionStorage.removeItem(STORAGE_KEY);
    setPreview(null);
    return userId;
  }, [preview]);

  const value = useMemo(
    () => ({
      isPreviewing: !!preview,
      previewPermissions: preview?.permissions || [],
      previewLabel: preview?.label || '',
      previewUserId: preview?.userId ?? null,
      previewEmployeeId: preview ? (preview.employeeId ?? null) : null,
      previewOverridesEmployeeLink: !!preview && preview.userId != null,
      canPreview,
      startPreview,
      stopPreview
    }),
    [preview, canPreview, startPreview, stopPreview]
  );

  return (
    <PermissionPreviewContext.Provider value={value}>
      {children}
    </PermissionPreviewContext.Provider>
  );
}

export function usePermissionPreview() {
  const context = useContext(PermissionPreviewContext);
  return context || {
    isPreviewing: false,
    previewPermissions: [],
    previewLabel: '',
    previewUserId: null,
    previewEmployeeId: null,
    previewOverridesEmployeeLink: false,
    canPreview: false,
    startPreview: () => false,
    stopPreview: () => null
  };
}
